export type BunkerPresetField = {
  label: string
  fieldType: string
  readOnly: boolean
  required: boolean
  active?: boolean
  unit?: string
}

export type BunkerPresetSection = {
  name: string
  fields: BunkerPresetField[]
}

export type BunkerRowAttachment = {
  documentName: string
  fileName: string
}

export type BunkerReceivedRow = {
  reportBlock: number
  allocationOnly?: boolean
  dateUtc: string
  timeUtc: string
  port: string
  fuelType: string
  quantity: string
  storageQuantity: string
  supplierName: string
  sulfurPercentage: string
  storage: string
  grade: string
  attachmentType: string
  attachments: BunkerRowAttachment[]
}

/** Must match the BDM option in the bunker attachment UI; enforced on save/submit. */
export const BUNKER_REQUIRED_ATTACHMENT_DOCUMENT_NAME = 'BDM ATTACHMENT (COMPULSORY)'

export const BUNKER_ATTACHMENT_TYPE_OPTIONS = [
  BUNKER_REQUIRED_ATTACHMENT_DOCUMENT_NAME,
  'LPO ATTACHMENT',
  'FUEL OIL ANALYSIS REPORT',
] as const

export const BUNKER_FUEL_TYPES = [
  'HFO',
  'VLSFO',
  'LSFO',
  'MGO',
  'MDO',
  'ULSFO',
  'LNG',
  'Methanol',
  'BioFuel',
  'LPG',
  'Hydrogen / Ammonia',
] as const

export const BUNKER_PRESET_LABELS = {
  details: {
    year: 'Year',
    vessel: 'Vessel',
  },
  quantities: {
    date: 'Date (UTC)',
    time: 'Time (UTC)',
    hfo: "HFO (MT's)",
    mgo: "MGO (MT's)",
  },
  received: {
    rows: 'Bunker Received Rows',
  },
  attachment: {
    rows: 'Bunker Attachments',
  },
} as const

export const BUNKER_PRESET_SECTIONS: BunkerPresetSection[] = [
  {
    name: 'Bunker Received',
    fields: [
      {label: BUNKER_PRESET_LABELS.details.year, fieldType: 'number', readOnly: true, required: true, active: true},
      {label: BUNKER_PRESET_LABELS.details.vessel, fieldType: 'text', readOnly: true, required: true, active: true},
      {label: BUNKER_PRESET_LABELS.quantities.date, fieldType: 'date', readOnly: false, required: true, active: true},
      {label: BUNKER_PRESET_LABELS.quantities.time, fieldType: 'time', readOnly: false, required: true, active: true},
      {label: BUNKER_PRESET_LABELS.quantities.hfo, fieldType: 'number', readOnly: false, required: true, active: true, unit: 'MT'},
      {label: BUNKER_PRESET_LABELS.quantities.mgo, fieldType: 'number', readOnly: false, required: true, active: true, unit: 'MT'},
      {label: BUNKER_PRESET_LABELS.received.rows, fieldType: 'textarea', readOnly: true, required: false, active: true},
      {label: BUNKER_PRESET_LABELS.attachment.rows, fieldType: 'textarea', readOnly: true, required: false, active: true},
    ],
  },
]

export const createEmptyBunkerRow = (): BunkerReceivedRow => ({
  reportBlock: 1,
  allocationOnly: false,
  dateUtc: '',
  timeUtc: '',
  port: '',
  fuelType: '',
  quantity: '',
  storageQuantity: '',
  supplierName: '',
  sulfurPercentage: '',
  storage: '',
  grade: '',
  attachmentType: '',
  attachments: [],
})

export const BUNKER_FIELD_LABEL_LOOKUP = {
  year: BUNKER_PRESET_LABELS.details.year,
  vessel: BUNKER_PRESET_LABELS.details.vessel,
  quantityDate: BUNKER_PRESET_LABELS.quantities.date,
  quantityTime: BUNKER_PRESET_LABELS.quantities.time,
  quantityHfo: BUNKER_PRESET_LABELS.quantities.hfo,
  quantityMgo: BUNKER_PRESET_LABELS.quantities.mgo,
  receivedRows: BUNKER_PRESET_LABELS.received.rows,
  attachmentRows: BUNKER_PRESET_LABELS.attachment.rows,
} as const
